import React, { useEffect, useState } from 'react';
import { Loader2, MapPin, Waves, Thermometer, Wind } from 'lucide-react';
import { api } from '@/lib/api';
import { SEA_LEVELS, SWIM_LEVELS, swimLabel, camName, windDirLabel } from '@/lib/tenerife';
import { ShareButton } from '@/components/ShareButton';

const T = {
  es: { beachTitle: 'Playas', beachSub: 'Ordenadas de mar más tranquilo a más bravo', surfTitle: 'Spots de surf', surfSub: 'Ordenados por calidad de las olas ahora', period: 'periodo', gusts: 'rachas', water: 'agua', error: 'Sin datos del mar ahora mismo', note: 'Modelo Open-Meteo de mar abierto ajustado por el abrigo de cada playa; comprueba siempre las banderas en la playa.', maps: 'Cómo llegar', level: { beginner: 'Iniciación', intermediate: 'Intermedio', advanced: 'Avanzado' }, brk: { beach: 'beach break', reef: 'reef', point: 'point break' }, score: ['Plato', 'Flojo', 'Surfeable', 'Bien', 'Muy bien', 'Épico'] },
  en: { beachTitle: 'Beaches', beachSub: 'From calmest to roughest sea', surfTitle: 'Surf spots', surfSub: 'Ranked by wave quality right now', period: 'period', gusts: 'gusts', water: 'water', error: 'No sea data right now', note: 'Open-Meteo open-sea model adjusted by each beach shelter; always check the flags on the beach.', maps: 'Directions', level: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' }, brk: { beach: 'beach break', reef: 'reef', point: 'point break' }, score: ['Flat', 'Weak', 'Surfable', 'Good', 'Very good', 'Epic'] },
  de: { beachTitle: 'Strände', beachSub: 'Von ruhigster bis rauester See', surfTitle: 'Surfspots', surfSub: 'Nach aktueller Wellenqualität', period: 'Periode', gusts: 'Böen', water: 'Wasser', error: 'Keine Meeresdaten im Moment', note: 'Open-Meteo-Modell für offenes Meer, angepasst an den Schutz jedes Strandes; immer die Flaggen am Strand beachten.', maps: 'Route', level: { beginner: 'Anfänger', intermediate: 'Mittel', advanced: 'Fortgeschritten' }, brk: { beach: 'Beachbreak', reef: 'Reef', point: 'Pointbreak' }, score: ['Flach', 'Schwach', 'Surfbar', 'Gut', 'Sehr gut', 'Episch'] },
};
const SCORE_COLORS = ['#A0AEC0', '#A0AEC0', '#D69E2E', '#38B2AC', '#2B6CB0', '#2B6CB0'];

const Stars = ({ n }) => (
  <span className="flex gap-0.5" aria-label={`${n}/5`}>
    {[1, 2, 3, 4, 5].map((i) => <span key={i} className="w-2 h-2 rounded-full" style={{ backgroundColor: i <= n ? SCORE_COLORS[n] : '#E2E8F0' }} />)}
  </span>
);

export const SpotsPanel = ({ kind, language = 'es', highlightId = null }) => {
  const t = T[language] || T.es;
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const isSurf = kind === 'surf';

  useEffect(() => {
    api.getSpotsSea().then((r) => setItems(r.data)).catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (items && highlightId) document.querySelector(`[data-testid="spot-row-${highlightId}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [items, highlightId]);

  const list = (items || [])
    .filter((s) => (isSurf ? s.surf : s.swim))
    .sort((a, b) => (isSurf ? b.surf_score - a.surf_score || b.wave_height - a.wave_height : ((SWIM_LEVELS[a.swim_level]?.rank ?? 2) - (SWIM_LEVELS[b.swim_level]?.rank ?? 2)) || a.wave_height - b.wave_height));

  return (
    <div className="bg-white shadow-md" data-testid={`spots-panel-${kind}`}>
      <div className="px-5 pt-5 pb-3">
        <p className="font-bold text-[#1D1D1F]">{isSurf ? t.surfTitle : t.beachTitle}</p>
        <p className="text-xs text-gray-500">{isSurf ? t.surfSub : t.beachSub}</p>
      </div>
      {error && <p className="px-5 pb-5 text-sm text-gray-500" data-testid="spots-error">{t.error}</p>}
      {!items && !error && <div className="pb-6 flex justify-center"><Loader2 className="animate-spin text-[#38B2AC]" /></div>}
      {items && (
        <div className="divide-y divide-gray-50" data-testid="spots-list">
          {list.map((s) => {
            const lv = SWIM_LEVELS[s.swim_level] || SEA_LEVELS[s.level_sea] || SEA_LEVELS.moderate;
            return (
              <div key={s.id} className={`px-5 py-3 flex items-center gap-3 ${s.id === highlightId ? 'bg-[#38B2AC]/5' : ''}`} data-testid={`spot-row-${s.id}`}>
                {isSurf ? <Stars n={s.surf_score} /> : <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: lv.color }} />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1D1D1F] truncate">{camName(s, language)} <span className="font-normal text-gray-400">· {s.zone}</span></p>
                  {isSurf ? (
                    <p className="text-xs truncate" style={{ color: SCORE_COLORS[s.surf_score] }}>
                      {t.score[s.surf_score]}{s.level ? ` · ${t.level[s.level]}` : ''}{s.break_type ? ` · ${t.brk[s.break_type]}` : ''}
                    </p>
                  ) : (
                    <p className="text-xs truncate" style={{ color: lv.color }} data-testid={`spot-swim-${s.id}`}>{swimLabel(s, language)}</p>
                  )}
                  <p className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Waves size={11} />{s.wave_height.toFixed(1)} m · {t.period} {Math.round(s.wave_period)} s</span>
                    <span className="inline-flex items-center gap-1" data-testid={`spot-wind-${s.id}`}><Wind size={11} />{Math.round(s.wind)} km/h {windDirLabel(s.wind_dir, language)}{s.wind_gust != null ? ` · ${t.gusts} ${Math.round(s.wind_gust)}` : ''}</span>
                    {s.sea_temp != null && <span className="inline-flex items-center gap-1"><Thermometer size={11} />{t.water} {Math.round(s.sea_temp)}°</span>}
                  </p>
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${s.latitude},${s.longitude}`} target="_blank" rel="noopener noreferrer"
                  className="p-2 text-[#38B2AC] hover:bg-[#38B2AC]/10 transition-colors flex-shrink-0" title={t.maps} data-testid={`spot-maps-${s.id}`}>
                  <MapPin size={18} />
                </a>
                <ShareButton kind="b" itemId={s.id} variant="ghost" size={18} className="flex-shrink-0 -ml-1" testId={`spot-share-${s.id}`} />
              </div>
            );
          })}
          <p className="px-5 py-3 text-[11px] text-gray-400" data-testid="spots-note">{t.note}</p>
        </div>
      )}
    </div>
  );
};

export default SpotsPanel;
